import { FC, useState, useMemo, useCallback, useEffect } from "react"
import { useDispatch, useSelector } from "react-redux"
import { Wearable, WearableModalType } from "./types"
import WearableConnectionInfo, {
   WearableConnectionStatus,
} from "./WearableConnectionInfo"
import WearableConnectedInfo from "./WearableConnectionInfo/WearableConnectedInfo"
import WearableContentInfo from "./WearableContentInfo"
import WearableIneligible from "./WearableIneligible"
import WearableAlreadyAssignedInfo from "./WearableIneligible/WearableAlreadyAssignedInfo"
import WearableUnknownInfo from "./WearableIneligible/WearableUnknownInfo"
import WearableInmateSelection from "./WearableInmateSelection"
import { WearableScannedInfo } from "./WearableInmateSelection/WearableScannedInfo"
import WearableManualSelection from "./WearableManualSelection"
import WearableNewInmateAssignment from "./WearableNewInmateAssignment"
import WearableOptionDisconnect from "./WearableOptionDisconnect"
import WearableOptionDisconnectInfo from "./WearableOptionDisconnect/WearableOptionDisconnectInfo"
import WearableScan from "./WearableScan"
import DualPanelModal from "@/components/DualPanelModal"
import { AppDispatch, RootState } from "@/store"
import { createInmate, selectInmates } from "@/store/inmatesSlice"
import {
   fetchWearableSensors,
   selectUnassignedWearableSensors,
   selectWearableSensorEntities,
   selectWearableSensorsByMacAddress,
} from "@/store/wearableSensorsSlice"
import { ColorVariant } from "@/types/index"
import { Sensor } from "@/types/inmate"
import { getFullInmateInfo, normalizeMacAddress } from "@/utils/index"
import "./index.scss"

interface WearablesModalProps {
   mode: WearableModalType
   originMode?: WearableModalType
   openModal: boolean
   handleCloseModal: () => void
   inmateId?: string | null
   wearable?: Sensor | null
   onSave?: (wearable: Wearable, inmateId?: string) => void
   onSaveScanned?: (inmateId: string) => void
   onClickDisconnect?: (inmateId: string, wearableId: string) => void
}

const WearablesModal: FC<WearablesModalProps> = ({
   mode,
   originMode,
   openModal,
   handleCloseModal,
   inmateId,
   wearable,
   onSave,
   onSaveScanned,
   onClickDisconnect,
}) => {
   const dispatch = useDispatch<AppDispatch>()
   const inmates = useSelector(selectInmates)
   const unassignedWearableSensors = useSelector(
      selectUnassignedWearableSensors
   )
   const wearableSensorEntities = useSelector(selectWearableSensorEntities)
   const wearableSensorsByMacAddress = useSelector(
      selectWearableSensorsByMacAddress
   )
   const { loading: loadingWearableSensors } = useSelector(
      ({ wearableSensorsReducer }: RootState) => wearableSensorsReducer
   )

   const [state, setState] = useState<WearableModalType>(mode)
   const [isLoading, setIsLoading] = useState(false)
   const [connectedWearableMacAddress, setConnectedWearableMacAddress] =
      useState<string | null>(null)
   const [customIdentifier, setCustomIdentifier] = useState<string | null>(null)

   const inmate = useMemo(
      () =>
         inmates.find((inmate) =>
            inmateId
               ? inmate.id === inmateId
               : inmate.sensor?.macAddress === wearable?.macAddress
         ),
      [inmates, inmateId, wearable]
   )

   const unassignedInmates = useMemo(
      () => inmates.filter((inmate) => !inmate.sensor),
      [inmates]
   )

   const wearables: Wearable[] = useMemo(
      () =>
         unassignedWearableSensors.map((sensor) => ({
            id: sensor.id,
            macAddress: sensor.macAddress,
            batteryPercentage: sensor.batteryPercentage,
         })),
      [unassignedWearableSensors]
   )

   const getBatteryPercentage = useCallback(
      (macAddress?: string | null, id?: string | null): number | null => {
         if (macAddress) {
            const sensor = wearableSensorsByMacAddress.get(
               normalizeMacAddress(macAddress)
            )
            if (sensor) return sensor.batteryPercentage
         }
         if (id) {
            return wearableSensorEntities[id]?.batteryPercentage ?? null
         }
         return null
      },
      [wearableSensorsByMacAddress, wearableSensorEntities]
   )

   useEffect(() => {
      if (
         openModal &&
         (state === WearableModalType.MANUAL_ASSIGN ||
            state === WearableModalType.MANUAL_SWAP)
      ) {
         dispatch(fetchWearableSensors())
      }
   }, [dispatch, openModal, state])

   useEffect(() => {
      if (openModal) {
         setState(mode)
         setCustomIdentifier(null)
         setConnectedWearableMacAddress(null)
      }
   }, [openModal, mode])

   const handleSave = useCallback(
      (wearableId: string) => {
         const stateTransitions: Partial<
            Record<WearableModalType, WearableModalType>
         > = {
            [WearableModalType.MANUAL_ASSIGN]: WearableModalType.ASSIGNED,
            [WearableModalType.MANUAL_SWAP]: WearableModalType.SWAPPED,
         }

         const nextState = stateTransitions[state]
         if (!nextState) return

         const selectedWearable = wearables.find(
            (wearable) => wearable.id === wearableId
         )
         setConnectedWearableMacAddress(
            selectedWearable?.macAddress ?? wearableId
         )
         setCustomIdentifier(inmate?.customIdentifier ?? "")
         setState(nextState)
         onSave?.(selectedWearable as Wearable, inmate?.id)
      },
      [onSave, state, wearables, inmate]
   )

   const handleDisconnect = useCallback(
      (wearableId: string) => {
         setConnectedWearableMacAddress(wearableId)
         setCustomIdentifier(inmate?.customIdentifier ?? "")
         setState(WearableModalType.DISCONNECTED)
         onClickDisconnect?.(inmate?.id ?? "", wearableId)
      },
      [onClickDisconnect, inmate]
   )

   const handleSaveScanned = useCallback(
      (selectedInmateId: string) => {
         const selectedInmate = unassignedInmates.find(
            (i) => i.id === selectedInmateId
         )
         setConnectedWearableMacAddress(wearable?.macAddress ?? "")
         setCustomIdentifier(selectedInmate?.customIdentifier ?? "")
         onSaveScanned?.(selectedInmateId)
         setState(WearableModalType.ASSIGNED)
      },
      [unassignedInmates, wearable, onSaveScanned]
   )

   const handleConnectNewInmate = useCallback(
      async (newInmateName: string) => {
         setIsLoading(true)
         try {
            const response = await dispatch(
               createInmate({ customIdentifier: newInmateName })
            ).unwrap()

            const newInmate = response.data
            setConnectedWearableMacAddress(wearable?.macAddress ?? "")
            setCustomIdentifier(newInmate.customIdentifier)
            onSaveScanned?.(newInmate.id)
            setState(WearableModalType.ASSIGNED)
         } catch (err) {
            console.error("Failed to connect new inmate:", err)
         } finally {
            setIsLoading(false)
         }
      },
      [dispatch, onSaveScanned, wearable]
   )

   const infoSection = useMemo(() => {
      switch (state) {
         case WearableModalType.OPTIONS:
            return (
               <WearableOptionDisconnectInfo
                  onClickManualSelection={() =>
                     setState(WearableModalType.MANUAL_SWAP)
                  }
               />
            )
         case WearableModalType.SCANNED:
         case WearableModalType.ASSIGN_NEW_INMATE:
            return (
               <WearableScannedInfo
                  wearableId={wearable?.macAddress ?? ""}
                  batteryPercentage={getBatteryPercentage(wearable?.macAddress)}
               />
            )
         case WearableModalType.ASSIGNED:
         case WearableModalType.SWAPPED:
         case WearableModalType.DISCONNECTED:
            return <WearableConnectedInfo type={state} />
         case WearableModalType.UNKNOWN:
            return (
               <WearableUnknownInfo
                  wearableId={
                     wearable?.serialNumber ?? inmate?.sensor?.id ?? ""
                  }
               />
            )
         case WearableModalType.ALREADY_ASSIGNED:
            return (
               <WearableAlreadyAssignedInfo
                  wearableId={wearable?.macAddress ?? inmate?.sensor?.id ?? ""}
                  batteryPercentage={getBatteryPercentage(
                     wearable?.macAddress,
                     inmate?.sensor?.id
                  )}
               />
            )
         default:
            return <WearableContentInfo type={state} />
      }
   }, [state, inmate, wearable, getBatteryPercentage])

   const actionSection = useMemo(() => {
      switch (state) {
         case WearableModalType.SCAN:
            return (
               <WearableScan
                  onManualSelection={() =>
                     setState(WearableModalType.MANUAL_ASSIGN)
                  }
               />
            )
         case WearableModalType.SCANNED:
            return (
               <WearableInmateSelection
                  inmates={unassignedInmates}
                  wearableId={wearable?.macAddress ?? ""}
                  batteryPercentage={getBatteryPercentage(wearable?.macAddress)}
                  onCancel={handleCloseModal}
                  onSave={handleSaveScanned}
                  onAddNewInmate={() =>
                     setState(WearableModalType.ASSIGN_NEW_INMATE)
                  }
               />
            )
         case WearableModalType.ASSIGN_NEW_INMATE:
            return (
               <WearableNewInmateAssignment
                  isLoading={isLoading}
                  wearableId={wearable?.macAddress ?? ""}
                  onSave={handleConnectNewInmate}
                  onBack={() => setState(WearableModalType.SCANNED)}
               />
            )
         case WearableModalType.MANUAL_ASSIGN:
         case WearableModalType.MANUAL_SWAP:
            return (
               <WearableManualSelection
                  wearables={wearables}
                  inmateId={
                     inmate?.customIdentifier || getFullInmateInfo(inmate)
                  }
                  onCancel={() =>
                     setState(originMode ?? WearableModalType.SCAN)
                  }
                  onSave={handleSave}
                  isLoading={loadingWearableSensors}
               />
            )
         case WearableModalType.OPTIONS:
            return (
               <WearableOptionDisconnect
                  wearableId={inmate?.sensor?.macAddress ?? ""}
                  batteryPercentage={getBatteryPercentage(
                     inmate?.sensor?.macAddress
                  )}
                  inmateId={
                     inmate?.customIdentifier || getFullInmateInfo(inmate)
                  }
                  onClickDisconnect={handleDisconnect}
               />
            )
         case WearableModalType.ASSIGNED:
         case WearableModalType.SWAPPED:
         case WearableModalType.DISCONNECTED:
            return (
               <WearableConnectionInfo
                  status={
                     state === WearableModalType.DISCONNECTED
                        ? WearableConnectionStatus.DISCONNECTED
                        : WearableConnectionStatus.CONNECTED
                  }
                  wearableId={
                     connectedWearableMacAddress ??
                     inmate?.sensor?.macAddress ??
                     ""
                  }
                  inmateId={
                     customIdentifier ||
                     inmate?.customIdentifier ||
                     getFullInmateInfo(inmate)
                  }
                  onClose={handleCloseModal}
               />
            )
         case WearableModalType.UNKNOWN:
            return (
               <WearableIneligible
                  {...(inmateId && {
                     onManualSelection: () =>
                        setState(WearableModalType.MANUAL_ASSIGN),
                  })}
                  {...(originMode && { onBack: () => setState(originMode) })}
               />
            )
         case WearableModalType.ALREADY_ASSIGNED:
            return (
               <WearableIneligible
                  onManualSelection={() =>
                     setState(
                        originMode === WearableModalType.SCAN
                           ? WearableModalType.MANUAL_ASSIGN
                           : WearableModalType.MANUAL_SWAP
                     )
                  }
                  onBack={() => setState(originMode ?? WearableModalType.SCAN)}
               />
            )
         default:
            return null
      }
   }, [
      state,
      unassignedInmates,
      wearable?.macAddress,
      handleCloseModal,
      handleSaveScanned,
      wearables,
      inmate,
      handleSave,
      isLoading,
      loadingWearableSensors,
      getBatteryPercentage,
      handleDisconnect,
      handleConnectNewInmate,
      connectedWearableMacAddress,
      customIdentifier,
      inmateId,
      originMode,
   ])

   return (
      <DualPanelModal
         open={openModal}
         variant={ColorVariant.COOL_GRAY}
         onClose={handleCloseModal}
         infoSection={infoSection}
         actionSection={actionSection}
      />
   )
}

export default WearablesModal
